<template>
  <div class="altcha-gate">
    <div class="altcha-gate__panel">
      <div class="altcha-gate__header">
        <div class="altcha-gate__title">{{ copy.title }}</div>
        <Popover
          v-model="showLanguagePopover"
          trigger="click"
          placement="bottom-end"
          :get-container="getLanguagePopoverContainer"
          class="altcha-gate__language"
        >
          <div class="altcha-gate__language-list">
            <button
              v-for="option in languageOptions"
              :key="option.value"
              type="button"
              class="altcha-gate__language-item"
              :class="{ active: option.value === selectedLocale }"
              @click="changeLanguage(option)"
            >
              <span>{{ option.label }}</span>
              <Icon name="success" size="16" v-show="option.value === selectedLocale" />
            </button>
          </div>
          <template #reference>
            <button type="button" class="altcha-gate__language-trigger" :aria-label="copy.languageLabel">
              <img :src="langUrl" class="lang-icon" alt="">
            </button>
          </template>
        </Popover>
      </div>
      <div class="altcha-gate__desc">{{ copy.desc }}</div>
      <altcha-widget
        ref="altchaWidget"
        challenge="/api/user/altcha/challenge"
        :language="altchaLanguage"
        :configuration="altchaConfiguration"
        auto="off"
        type="checkbox"
      ></altcha-widget>
      <div v-if="message" class="altcha-gate__message">{{ message }}</div>
    </div>
  </div>
</template>

<script>
import 'altcha/dist/main/altcha.i18n.js'
import { Icon, Popover, Locale } from 'vant'
import enUS from 'vant/es/locale/lang/en-US'
import zhTW from 'vant/es/locale/lang/zh-TW'
import { altchaStatus, verifyAltcha } from '@/api/altcha'
import { applyVantLocale } from '@/utils/vantLocale'
import {
  clearAltchaToken,
  extractAltchaPayload,
  getAltchaConfiguration,
  getAltchaCopy,
  getAltchaLanguageOptions,
  getCurrentLocale,
  getAltchaLanguage,
  getAltchaToken,
  isAltchaVerificationValid,
  setAltchaLocale,
  setAltchaToken
} from '@/utils/altchaGate'

export default {
  name: 'AltchaGate',
  components: {
    Icon,
    Popover
  },
  data() {
    return {
      message: '',
      langUrl: require('../../assets/imgs/icon-lang.png'),
      showLanguagePopover: false,
      selectedLocale: getCurrentLocale(),
      languageOptions: getAltchaLanguageOptions(),
      altchaLanguage: getAltchaLanguage(),
      altchaConfiguration: getAltchaConfiguration(),
      copy: getAltchaCopy()
    }
  },
  mounted() {
    this.bindWidget()
    this.checkSession()
  },
  beforeDestroy() {
    this.unbindWidget()
  },
  methods: {
    bindWidget() {
      this.$nextTick(() => {
        const widget = this.$refs.altchaWidget
        if (!widget || !widget.addEventListener) return
        widget.addEventListener('verified', this.onVerified)
        widget.addEventListener('expired', this.onExpired)
      })
    },
    unbindWidget() {
      const widget = this.$refs.altchaWidget
      if (!widget || !widget.removeEventListener) return
      widget.removeEventListener('verified', this.onVerified)
      widget.removeEventListener('expired', this.onExpired)
    },
    async checkSession() {
      const token = getAltchaToken()
      if (!token) return
      try {
        const res = await altchaStatus({ token })
        if (res && res.code === 0 && res.data && res.data.verified === true) {
          this.finishVerification()
          return
        }
      } catch (error) {
        console.error('ALTCHA status check failed:', error)
      }
      clearAltchaToken()
    },
    finishVerification() {
      this.message = ''
      this.$emit('verified')
    },
    onExpired() {
      this.message = this.copy.expired
      this.resetWidget()
    },
    getLanguagePopoverContainer() {
      return this.$el
    },
    changeLanguage(option) {
      const locale = setAltchaLocale(option.value)
      this.selectedLocale = locale
      this.showLanguagePopover = false
      this.applyAppLanguage(locale)
      this.refreshLanguage()
      this.message = ''
      this.resetWidget()
    },
    async onVerified(event) {
      const payload = extractAltchaPayload(event, this.$refs.altchaWidget)
      if (!payload) {
        this.message = this.copy.invalid
        this.resetWidget()
        return
      }

      try {
        const res = await verifyAltcha({ altcha: payload })
        if (!isAltchaVerificationValid(res)) {
          this.message = this.copy.failed
          this.resetWidget()
          return
        }
        setAltchaToken(res.data.token)
        this.finishVerification()
      } catch (error) {
        console.error('ALTCHA verification failed:', error)
        this.message = this.copy.failed
        this.resetWidget()
      }
    },
    refreshLanguage() {
      this.selectedLocale = getCurrentLocale()
      this.altchaLanguage = getAltchaLanguage()
      this.copy = getAltchaCopy()
    },
    applyAppLanguage(locale) {
      const option = this.languageOptions.find(item => item.value === locale)
      const localeName = option ? option.label : 'English'
      if (this.$i18n) {
        this.$i18n.locale = locale
      }
      applyVantLocale(locale, Locale, enUS, zhTW)
      if (this.$store && this.$store.state) {
        this.$store.state.language = locale
        this.$store.state.langName = localeName
      }
      if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.setAttribute('lang', locale)
      }
    },
    resetWidget() {
      this.$nextTick(() => {
        const widget = this.$refs.altchaWidget
        if (widget && typeof widget.reset === 'function') {
          widget.reset()
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.altcha-gate {
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #F8F9FA;
}

.altcha-gate__panel {
  width: 100%;
  max-width: 420px;
  padding: 28px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
}

.altcha-gate__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.altcha-gate__title {
  flex: 1;
  color: #111827;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.altcha-gate__language {
  flex-shrink: 0;
  display: inline-flex;
}

.altcha-gate__language-trigger {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #E5E7EB;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  cursor: pointer;
}

.lang-icon {
  width: 22px;
  height: 22px;
  display: block;
}

.altcha-gate__language-list {
  width: 180px;
  max-height: 260px;
  overflow: auto;
  padding: 6px 0;
}

.altcha-gate__language-item {
  width: 100%;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: #111827;
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}

.altcha-gate__language-item:hover {
  background: #F3F4F6;
}

.altcha-gate__language-item.active {
  background: rgba(0, 114, 245, 0.08);
  color: #0072F5;
  font-weight: 600;
}

.altcha-gate__desc {
  margin: 8px 0 20px;
  color: #6B7280;
  font-size: 14px;
  line-height: 20px;
}

.altcha-gate__message {
  margin-top: 12px;
  color: #DC2626;
  font-size: 13px;
  line-height: 18px;
}

altcha-widget {
  display: block;
}

@media (max-width: 420px) {
  .altcha-gate__header {
    gap: 10px;
  }
}
</style>
